import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Checkbox,
  Alert,
  Box,
  Typography,
  Divider,
  ToggleButton,
  ToggleButtonGroup,
  Paper,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import leaveApiService from "../services/leave-api";
import { LeaveType, LeaveBalance } from "../types/leave";

interface LeaveGrantDialogProps {
  open: boolean;
  onClose: () => void;
  employeeId: number | null;
  employeeName: string;
  onSuccess: () => void;
}

interface LeaveGrantForm {
  leaveTypeCode: string;
  leaveTypeId?: number; // Added to support balance adjustment API
  year: number;
  daysToGrant: number;
  reason: string;
  addToExisting: boolean;
  operation: "add" | "subtract"; // New field to specify operation
}

const LeaveGrantDialog: React.FC<LeaveGrantDialogProps> = ({
  open,
  onClose,
  employeeId,
  employeeName,
  onSuccess,
}) => {
  const { t } = useTranslation();
  const currentYear = new Date().getFullYear();

  const [formData, setFormData] = useState<LeaveGrantForm>({
    leaveTypeCode: "",
    leaveTypeId: undefined,
    year: currentYear,
    daysToGrant: 0,
    reason: "",
    addToExisting: true,
    operation: "add",
  });

  const [leaveTypes, setLeaveTypes] = useState<LeaveType[]>([]);
  const [currentBalances, setCurrentBalances] = useState<LeaveBalance[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Leave types that can be granted/adjusted by Admin
  const grantableLeaveTypes = [
    "VACATION",
    "PATERNITY",
    "MARRIAGE_SELF",
    "BEREAVEMENT_IMM",
    "BEREAVEMENT_EXT",
    "BEREAVEMENT_DIST",
    "MATERNITY",
    "UNPAID",
  ];

  useEffect(() => {
    if (open) {
      loadLeaveTypes();
      if (employeeId) {
        loadCurrentBalances();
      }
    }
  }, [open, employeeId]);

  const loadLeaveTypes = async () => {
    try {
      const types = await leaveApiService.getActiveLeaveTypes();
      setLeaveTypes(
        types.filter((type) => grantableLeaveTypes.includes(type.code)),
      );
    } catch {
      setError("Failed to load leave types");
    }
  };

  const loadCurrentBalances = async () => {
    if (!employeeId) return;

    try {
      const balances = await leaveApiService.getEmployeeBalancesAdmin(
        employeeId,
        formData.year,
      );
      setCurrentBalances(balances);
    } catch {
      // Silently fail - balances are optional
    }
  };

  const handleSubmit = async () => {
    if (!employeeId || !formData.leaveTypeCode || formData.daysToGrant === 0) {
      setError(t("error.fillAllRequiredFields"));
      return;
    }

    setLoading(true);
    setError(null);

    try {
      if (formData.operation === "subtract" && formData.leaveTypeId) {
        // Use balance adjustment API for subtract operations
        await leaveApiService.adjustLeaveBalance({
          employeeId,
          leaveTypeId: formData.leaveTypeId,
          year: formData.year,
          adjustmentAmount: Math.abs(formData.daysToGrant),
          adjustmentType: "SUBTRACT_ALLOCATED",
          reason: formData.reason || t("common.adminAdjustment"),
        });
      } else {
        // Use grant leave API for add operations
        await leaveApiService.grantLeaveBalance({
          employeeId,
          leaveTypeCode: formData.leaveTypeCode,
          year: formData.year,
          daysToGrant: Math.abs(formData.daysToGrant),
          reason: formData.reason,
          addToExisting: formData.addToExisting,
        });
      }

      onSuccess();
      handleClose();
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : t("error.failedToUpdateLeaveBalance"),
      );
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setFormData({
      leaveTypeCode: "",
      leaveTypeId: undefined,
      year: currentYear,
      daysToGrant: 0,
      reason: "",
      addToExisting: true,
      operation: "add",
    });
    setError(null);
    onClose();
  };

  const getCurrentBalance = (leaveTypeCode: string) => {
    const balance = currentBalances.find(
      (b) => b.leaveTypeCode === leaveTypeCode,
    );
    return balance ? balance.allocatedDays : 0;
  };

  const getLeaveTypeName = (code: string) => {
    const leaveType = leaveTypes.find((lt) => lt.code === code);
    return leaveType ? leaveType.name : code;
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>{t("leave.editLeaveCredits")}</DialogTitle>

      <DialogContent>
        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle1" color="primary">
            {t("leave.employee")}: {employeeName}
          </Typography>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
          {/* Operation Selection - Prominent Toggle */}
          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
              {t("leave.selectOperation")}
            </Typography>
            <ToggleButtonGroup
              value={formData.operation}
              exclusive
              onChange={(e, newOperation) => {
                if (newOperation !== null) {
                  setFormData({
                    ...formData,
                    operation: newOperation as "add" | "subtract",
                  });
                }
              }}
              fullWidth
              sx={{ mb: 1 }}
            >
              <ToggleButton value="add" aria-label="add">
                <AddIcon sx={{ mr: 1 }} />
                {t("leave.addCredits")}
              </ToggleButton>
              <ToggleButton value="subtract" aria-label="subtract">
                <RemoveIcon sx={{ mr: 1 }} />
                {t("leave.subtractCredits")}
              </ToggleButton>
            </ToggleButtonGroup>
          </Box>

          <Divider />

          {/* Leave Type Selection */}
          <FormControl fullWidth required>
            <InputLabel>{t("leave.leaveType")}</InputLabel>
            <Select
              value={formData.leaveTypeCode}
              onChange={(e) => {
                const selectedType = leaveTypes.find(
                  (t) => t.code === e.target.value,
                );
                setFormData({
                  ...formData,
                  leaveTypeCode: e.target.value,
                  leaveTypeId: selectedType?.id,
                });
              }}
              label={t("leave.leaveType")}
            >
              {leaveTypes.map((type) => (
                <MenuItem key={type.code} value={type.code}>
                  {type.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Current Balance Display */}
          {formData.leaveTypeCode && (
            <Paper
              sx={{
                p: 2,
                bgcolor: "info.lighter",
                border: "1px solid",
                borderColor: "info.light",
              }}
            >
              <Typography variant="body2" color="text.secondary">
                {t("leave.currentBalance")}
              </Typography>
              <Typography variant="h6" color="primary" sx={{ fontWeight: 600 }}>
                {getCurrentBalance(formData.leaveTypeCode)} {t("leave.days")}
              </Typography>
            </Paper>
          )}

          {/* Year Selection */}
          <TextField
            label={t("leave.year")}
            type="number"
            value={formData.year}
            onChange={(e) =>
              setFormData({ ...formData, year: parseInt(e.target.value) })
            }
            required
            fullWidth
          />

          {/* Days Input - Dynamic Label */}
          <TextField
            label={
              formData.operation === "subtract"
                ? t("leave.daysToSubtract")
                : t("leave.daysToAdd")
            }
            type="number"
            value={formData.daysToGrant}
            onChange={(e) =>
              setFormData({
                ...formData,
                daysToGrant: parseFloat(e.target.value) || 0,
              })
            }
            required
            fullWidth
            inputProps={{ min: 0.5, step: 0.5 }}
            helperText={
              formData.operation === "subtract"
                ? t("leave.subtractHelp")
                : t("leave.addHelp")
            }
          />

          {/* Add to Existing - Only show for Add operation */}
          {formData.operation === "add" && (
            <FormControlLabel
              control={
                <Checkbox
                  checked={formData.addToExisting}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      addToExisting: e.target.checked,
                    })
                  }
                />
              }
              label={t("leave.addToExisting")}
            />
          )}

          {/* Reason */}
          <TextField
            label={t("leave.reason")}
            multiline
            rows={3}
            value={formData.reason}
            onChange={(e) =>
              setFormData({ ...formData, reason: e.target.value })
            }
            fullWidth
            placeholder={t("leave.reasonPlaceholder")}
          />
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose}>{t("common.cancel")}</Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={
            loading || !formData.leaveTypeCode || formData.daysToGrant === 0
          }
        >
          {loading
            ? t("common.updating")
            : formData.operation === "subtract"
              ? t("leave.subtractCredits")
              : t("leave.addCredits")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default LeaveGrantDialog;
